import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  expensesApi,
  payersApi,
  Expense,
  ExpensesSummary,
  Payer,
  CreateExpenseDto,
} from '../../../api/expenses';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { UseExpensesReturn } from '../types';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): CreateExpenseDto => ({
  date: todayISO(),
  title: '',
  description: '',
  amount: 0,
  payerId: '',
});

const emptySummary: ExpensesSummary = { total: 0, byPayer: [] };

export function useExpenses(): UseExpensesReturn {
  const confirm = useConfirm();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<ExpensesSummary>(emptySummary);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPayersDialogOpen, setIsPayersDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState<CreateExpenseDto>(emptyForm());
  const [newPayerName, setNewPayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [list, payersList] = await Promise.all([
        expensesApi.getAll(),
        payersApi.getAll(),
      ]);
      setExpenses(list.expenses);
      setSummary(list.summary);
      setPayers(payersList);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить расходы');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setFormData({ ...emptyForm(), payerId: payers[0]?.id || '' });
    setIsCreateDialogOpen(true);
  };

  const validate = (): boolean => {
    if (!formData.title.trim()) {
      toast.error('Укажите заголовок');
      return false;
    }
    if (!formData.payerId) {
      toast.error('Выберите плательщика');
      return false;
    }
    if (!formData.amount || formData.amount <= 0) {
      toast.error('Сумма должна быть больше 0');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (isSubmitting || !validate()) return;
    setIsSubmitting(true);
    try {
      await expensesApi.create(formData);
      toast.success('Расход добавлен');
      setIsCreateDialogOpen(false);
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось добавить расход');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date,
      title: expense.title,
      description: expense.description || '',
      amount: expense.amount,
      payerId: expense.payerId,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingExpense || isSubmitting) return;
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await expensesApi.update(editingExpense.id, formData);
      toast.success('Расход обновлён');
      setIsEditDialogOpen(false);
      setEditingExpense(null);
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось обновить расход');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить расход?',
      description: 'Это действие необратимо.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await expensesApi.delete(id);
      toast.success('Расход удалён');
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось удалить расход');
    }
  };

  const handleAddPayer = async () => {
    const name = newPayerName.trim();
    if (!name) {
      toast.error('Введите имя плательщика');
      return;
    }
    try {
      await payersApi.create(name);
      toast.success('Плательщик добавлен');
      setNewPayerName('');
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось добавить плательщика');
    }
  };

  const handleDeletePayer = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить плательщика?',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await payersApi.delete(id);
      toast.success('Плательщик удалён');
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось удалить плательщика');
    }
  };

  return {
    expenses,
    summary,
    payers,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    isPayersDialogOpen,
    editingExpense,
    formData,
    newPayerName,
    isSubmitting,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setIsPayersDialogOpen,
    setFormData,
    setNewPayerName,
    openCreate,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleAddPayer,
    handleDeletePayer,
  };
}
